import React from 'react'
import { MessageItemModel, RequestItemModel } from '../requestsModel'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import styles from '../../../styles'
import { useMediaQuery } from 'react-responsive'
import { FormattedMessage, useIntl } from 'react-intl'
import { Administrative, Attachment, Technical } from '../../../icons'
import { formatDate } from '../../../utils/formatDate'
import { useHistory } from 'react-router'
import links from '../../../enums/linksEnum'
import { getRequestCategory } from '../requestMapper'

const useStyle = makeStyles(
	(theme) => (
		{
			...styles(theme),
			listStyle: {
				background: 'white',
				marginBottom: 16,
				cursor: 'pointer',

				'& div p': {
					margin: 0
				}
			},
			listLargeScreenStyle: {
				padding: '35px 35px 35px 50px'
			},
			listSmallScreenStyle: {
				padding: 18
			},
			titleContainer: {
				display: 'flex'
			},
			attachmentIcon: {
				marginLeft: 6
			},
			dateColumn: {
				display: 'flex',
				flexDirection: 'row-reverse'
			},
			dateContainer: {
				width: 187,
				height: 32,
				borderRadius: 15,
				backgroundColor: theme.palette.blue['10'],
				display: 'flex',
				justifyContent: 'center'
			},
			dateText: {
				margin: 'auto'
			},
			marginBottomBloc: {
				marginBottom: 10
			}
		}
	)
)

interface AcceptingProps {
	request: RequestItemModel
}

type RequestProps = AcceptingProps

export const LEAVE_DEPOSIT_CATEGORY = 'contract_leaveDeposit'
export const ASSURANCE_CATEGORY = 'contract_assurance'

const RequestCard: React.FC<RequestProps> = (
	{
		request
	}
) => {
	const classes = useStyle()
	const theme = useTheme()
	const intl = useIntl()
	const history = useHistory()
	const isLargeScreen = useMediaQuery(
		{
			query: '(min-width: 1025px)'
		}
	)

	const listClass: ClassValue = classNames(
		'row middle-xs between-xs',
		classes.listStyle,
		{
			[classes.listLargeScreenStyle]: isLargeScreen,
			[classes.listSmallScreenStyle]: !isLargeScreen
		}
	)

	const titleClass: ClassValue = classNames(
		classes.colorBlue,
		isLargeScreen ? classes.text0 : classes.text7
	)

	const categoryClass: ClassValue = classNames(
		classes.colorBlue,
		isLargeScreen ? classes.text1_opacity : classes.text2_opacity
	)

	const iconSmallContainerClass: ClassValue = classNames(
		'col-xs-12 row between-xs',
		classes.marginBottomBloc
	)

	const infoSmallContainerClass: ClassValue = classNames(
		'col-xs-12 row between-xs',
		classes.marginBottomBloc
	)

	const goToDetail = () => {
		history.push(`${links.requests}/${request.id}`)
	}

	let labelCategory = ''
	let subCategory = ''
	let isTechnical = false

	// Gestion du display de la catégorie
	if (request.category === LEAVE_DEPOSIT_CATEGORY) {
		labelCategory = `${intl.formatMessage({
			id: 'requests.makeRequest.subHeader.administrative',
			defaultMessage: 'Demande administrative',
			description: 'Administrative label'
		})} - ${intl.formatMessage({
			id: 'requests.makeRequest.step2.contract',
			defaultMessage: 'Vie du contrat',
			description: 'Contract label'
		})} - ${intl.formatMessage({
			id: 'requests.makeRequest.step3.deposit',
			defaultMessage: 'Résiliation du bail',
			description: 'Leave deposit label'
		})}`
	} else if (request.category === ASSURANCE_CATEGORY) {
		labelCategory = `${intl.formatMessage({
			id: 'requests.makeRequest.subHeader.administrative',
			defaultMessage: 'Demande administrative',
			description: 'Administrative label'
		})} - ${intl.formatMessage({
			id: 'requests.makeRequest.step2.contract',
			defaultMessage: 'Vie du contrat',
			description: 'Contract label'
		})} - ${intl.formatMessage({
			id: 'requests.makeRequest.step3.assurance',
			defaultMessage: 'Attestation d\'assurance habitation',
			description: 'Assurance label'
		})}`
	} else {
		const requestCategory = getRequestCategory(request.category, intl)
		labelCategory = requestCategory.labelCategory
		isTechnical = requestCategory.isTechnical
		subCategory = requestCategory.subCategory
	}

	// Gestion du badge de date
	let updateLabelProps = {
		id: 'requests.createdAt',
		defaultMessage: 'Créée',
		description: 'Date de création'
	}
	if (request.cloture) {
		updateLabelProps = {
			id: 'requests.closedAt',
			defaultMessage: 'Clôturée',
			description: 'Date de clôture'
		}
	} else if (request.annule) {
		updateLabelProps = {
			id: 'requests.canceledAt',
			defaultMessage: 'Annulée',
			description: 'Date d\'annulation'
		}
	} else if (request.messages.length > 1) {
		updateLabelProps = {
			id: 'requests.updatedAt',
			defaultMessage: 'Mise à jour',
			description: 'Date de mise à jour'
		}
	}

	const requestHasAttachment: boolean = !!request.messages.find((message: MessageItemModel) => message.pj)

	const iconProp = {
		color: theme.palette.blue.main,
		width: isLargeScreen ? 50 : 21,
		height: isLargeScreen ? 50 : 23
	}

	return <div className={listClass} onClick={goToDetail}>
		{isLargeScreen ?
			<>
				<div className="col-md-1">
					{isTechnical ? <Technical {...iconProp} /> : <Administrative {...iconProp} />}
				</div>

				<div className="col-md-8">
					<div className={classes.titleContainer}>
						<p className={titleClass}>{subCategory}</p>
						{requestHasAttachment &&
							<span className={classes.attachmentIcon}><Attachment color={theme.palette.blue.main} width={25}
							                                                     height={25} /></span>}
					</div>
					<p className={categoryClass}>
						{labelCategory}
					</p>
				</div>
				<div className={classNames('col-md-3', classes.dateColumn)}>
					<div className={classes.dateContainer}>
				        <span className={classNames(classes.text4_opacity, classes.dateText)}><FormattedMessage
					        {...updateLabelProps}
					        values={
						        {
							        date: formatDate(request.messages.length > 0 ? request.messages[request.messages.length - 1].modifiedOn : request.modifiedOn, intl.locale)
						        }
					        }
				        />
				        </span>
					</div>
				</div>
			</>
			:
			<>
				<div className={iconSmallContainerClass}>
					{isTechnical ? <Technical {...iconProp} /> : <Administrative {...iconProp} />}
					{requestHasAttachment &&
						<span className={classes.attachmentIcon}><Attachment color={theme.palette.blue.main} width={25}
						                                                     height={25} /></span>}
				</div>

				<div className={infoSmallContainerClass}>
					<div className={classes.titleContainer}>
						<p className={titleClass}>{subCategory}</p>
					</div>
					<p className={categoryClass}>
						{labelCategory}
					</p>
				</div>
				<div className={classNames('col-xs-5 row', classes.dateColumn)}>
					<div className={classes.dateContainer}>
        <span className={classNames(classes.text16_opacity, classes.dateText)}><FormattedMessage
	        {...updateLabelProps}
	        values={
		        {
			        date: formatDate(request.messages.length > 1 ? request.messages[request.messages.length - 1].modifiedOn : request.modifiedOn, intl.locale)
		        }
	        }
        />
        </span>

					</div>
				</div>
			</>

		}


	</div>
}

export default RequestCard
